import type { ChatMessage } from '@erp/shared';

/**
 * O que cada mensagem é na conversa (spec 060), e não só quem a escreveu. A
 * tela separa três coisas que antes saíam no mesmo amarelo:
 *
 * - `internal-comment`: o comentário interno de um ATENDENTE (amarelo);
 * - `system-note`: o registro do SISTEMA com detalhes (a transferência do bot
 *   externo, com variáveis e a conversa com ele; a conversa na Blip antes do
 *   nosso bot). Fica recolhido numa linha, e o switch "Detalhes do sistema"
 *   mostra tudo;
 * - `automatic`: a mensagem que o sistema MANDOU ao cliente (o modelo do
 *   disparo, a saudação e o menu do bot do ERP). É conversa, e sai como balão.
 *
 * O resto: a linha de evento (`system-event`: "Fulano assumiu a conversa",
 * "Finalizada: motivo"), a fala do bot externo (`bot`) e a conversa de sempre.
 */
export type MessageKind =
  | 'conversation'
  | 'internal-comment'
  | 'bot'
  | 'automatic'
  | 'system-note'
  | 'system-event';

type KindSource = Pick<ChatMessage, 'authorType' | 'internal' | 'text' | 'deliveryStatus' | 'wamid'>;

export function messageKind(message: KindSource): MessageKind {
  if (message.authorType === 'bot') return 'bot';
  if (message.authorType !== 'system') return message.internal ? 'internal-comment' : 'conversation';
  const multiline = message.text.includes('\n');
  if (message.internal) return multiline ? 'system-note' : 'system-event';
  // saiu para o cliente: tem o tique (ou o id da Meta). E a de várias linhas
  // nunca é linha de evento — é o modelo ou o menu antes de o envio responder
  if (message.deliveryStatus !== null || message.wamid !== null || multiline) return 'automatic';
  return 'system-event';
}

/** a linha que representa a nota recolhida: a primeira que tem texto */
export function noteSummary(text: string): string {
  return (
    text
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line !== '') ?? ''
  );
}

/** o switch só aparece na conversa que tem o que esconder */
export function hasSystemNotes(messages: readonly KindSource[]): boolean {
  return messages.some((m) => messageKind(m) === 'system-note');
}

/** A chave do switch no navegador — por navegador, nunca no servidor. */
export const SYSTEM_DETAILS_KEY = 'erp.chats.detalhesSistema';

type PrefStorage = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * A última escolha do switch (o padrão da Agenda, `rememberedShowHidden`):
 * `storage` é uma função porque o próprio acesso a `window.localStorage` lança
 * com os dados do site bloqueados. Sem storage, ilegível ou nunca gravado:
 * desligado — as notas do sistema começam recolhidas.
 */
export function rememberedSystemDetails(storage: () => PrefStorage | undefined): boolean {
  try {
    return storage()?.getItem(SYSTEM_DETAILS_KEY) === '1';
  } catch {
    return false;
  }
}

/** Grava a escolha; falhar ao gravar não é erro de tela — vale até recarregar. */
export function rememberSystemDetails(storage: () => PrefStorage | undefined, on: boolean): void {
  try {
    storage()?.setItem(SYSTEM_DETAILS_KEY, on ? '1' : '0');
  } catch {
    // aba privada, cota cheia, dados bloqueados: fica só nesta tela
  }
}
